import appManager from '@app/appManager';
import AppModule from '@app/lib/AppModule';
import { proxy } from 'valtio';
import {
  ToastState,
  ToastOptions,
  Toast,
  IncomingCallToastType,
} from './types';
import { valtioDevTools } from '@lib/utils/devTools';

export const toastsState = proxy<ToastState>({
  toasts: [],
  incomingCallToasts: [],
});

valtioDevTools(toastsState, 'toastsState');

class ToastModule extends AppModule {
  /**
   * Provides an api for the application to render Anvil Toasts.
   * Toasts will be displayed in the order they are invoked.
   * With the newest toasts at the top.
   * The default duration is 8 seconds, inline with the Anvil Toast's default duration.
   * @param toast - The toast options.
   */
  public showToast(toast: ToastOptions) {
    // if the toast already exists, do not add it again
    if (toast.id && toastsState.toasts.find((t) => t.id === toast.id)) {
      return;
    }

    const newToast: Toast = {
      ...toast,
      duration: toast.duration || 8000, // default duration is 8 seconds to match anvil's default duration
      createdAt: Date.now(),
    };

    const newToasts = [newToast, ...toastsState.toasts];
    toastsState.toasts = newToasts;
  }

  public impersonatedUserToast() {
    this.showToast({
      status: 'critical',
      title: 'Action not allowed',
      toastBody:
        'This action cannot be performed while impersonating another user.',
    });
  }

  public hideToast(createdAt: number) {
    // remove toast from state
    const newToasts = [...toastsState.toasts].filter(
      (t) => t.createdAt !== createdAt,
    );
    toastsState.toasts = newToasts;
  }

  /**
   * Displays an incoming call toast notification if it is not already present in the toasts state.
   *
   * @param {IncomingCallToastType} callToast - The incoming call toast object containing the details of the call.
   * @returns {void}
   */
  public showIncomingCallToast(callToast: IncomingCallToastType) {
    if (!toastsState.incomingCallToasts.find((t) => t.id === callToast.id)) {
      const newToasts = [callToast, ...toastsState.incomingCallToasts];
      toastsState.incomingCallToasts = newToasts;
    }
  }

  /**
   * Hides an incoming call toast notification based on the call ID or conversation SID.
   *
   * The reason for this is that the toast can be shown by the softphone callStarted event (callId)
   * or by the sessionOffered event (conversationSid).
   *
   * @param {string} callId - The ID of the call to hide the toast for.
   * @param {string} conversationSid - The SID of the conversation to hide the toast for.
   * @returns {void}
   */
  public hideIncomingCallToast(
    callId: string = '',
    conversationSid: string = '',
  ) {
    const newCallToasts = [...toastsState.incomingCallToasts].filter(
      (t) => t.id !== callId && t.id !== conversationSid,
    );
    toastsState.incomingCallToasts = newCallToasts;
  }
}

export default new ToastModule(appManager.events, appManager.logger('Toasts'));
